// RITA-1504 (Nutzungserfassung Phase 2b) — Wächter für die Zusatzfelder (props).
//
// batchSchema.ts im Backend ist strict: ein einziges unbekanntes oder ungültiges Feld lässt den
// Server das GANZE Bündel (bis 100 Ereignisse) verwerfen — die Nutzung anderer Bedienschritte
// wäre damit still weg. Deshalb baut kein Erzeuger seine props selbst, sondern ausschließlich
// über die build*-Funktionen hier:
//  - feste Schlüssel-Whitelist je Art (unbekannte Felder werden nicht übernommen),
//  - Zahlen nur als nichtnegative Ganzzahlen; ein ungültiger Wert (−1, NaN, Infinity) lässt das
//    Optionalfeld entfallen, statt das Bündel zu gefährden,
//  - Zeichenketten gekürzt (Spalte 64, Feldliste 256, Name 128, Elementart 16),
//  - es gelangen nur Feld- und Spaltennamen hinein, nie Eingaben aus der Bedienung.

/** Grenzen aus batchSchema.ts (propString(64) / propString(256) / shortName). */
export const MAX_COLUMN_LENGTH = 64;
/** Grenze aus batchSchema.ts (propString(16)) — dieselbe wie beim Klick-Ereignis. */
export const MAX_ELEMENT_KIND_LENGTH = 16;
export const MAX_INVALID_FIELDS_LENGTH = 256;
export const MAX_USAGE_NAME_LENGTH = 128;
/** Ersatzname, falls ein Erzeuger keinen brauchbaren Namen liefert (name ist Pflicht, min 1). */
export const UNKNOWN_USAGE_NAME = 'unbekannt';

export type TableOp = 'sort' | 'filter' | 'page' | 'page_size' | 'columns' | 'export' | 'group';
export type FormOp = 'open' | 'submit_ok' | 'submit_fail' | 'abandon';
export type DialogOp = 'confirm' | 'cancel';

// Entspricht tableProps in batchSchema.ts (strict): genau diese Felder.
// Typ-Alias statt Interface, damit der Wert als Record<string, UsagePropValue> durchgeht.
export type TableProps = {
  op: TableOp;
  column?: string;
  page_size?: number;
  page?: number;
  hidden_count?: number;
};

// Entspricht formProps in batchSchema.ts (strict): genau diese Felder.
export type FormProps = {
  op: FormOp;
  ms_open?: number;
  changed_fields?: number;
  invalid_fields?: string;
};

// Entspricht searchProps in batchSchema.ts (strict): genau diese Felder.
export type SearchProps = {
  query_len: number;
  results: number;
  selected_rank?: number;
};

// Entspricht dialogProps in batchSchema.ts (strict): genau dieses Feld.
export type DialogProps = {
  op: DialogOp;
};

// Entspricht rageClickProps bzw. deadClickProps in batchSchema.ts (strict): genau dieses Feld.
// Beide Arten tragen dasselbe einzige Zusatzfeld; der Ort steckt im Namen, die Zahl der Klicks
// wird bewusst NICHT gesendet (kein Leser im Rollup — RITA-1500, Entscheidung W1).
export type ElementKindProps = {
  element_kind: string;
};

export interface TablePropsInput {
  op: TableOp;
  column?: string;
  page_size?: number;
  page?: number;
  hidden_count?: number;
}

export interface FormPropsInput {
  op: FormOp;
  ms_open?: number;
  changed_fields?: number;
  /** Nur Feld-NAMEN; sie werden bereinigt, entdoppelt und sortiert zusammengefügt. */
  invalid_fields?: readonly string[];
}

export interface SearchPropsInput {
  query_len: number;
  results: number;
  selected_rank?: number;
}

export interface DialogPropsInput {
  op: DialogOp;
}

export interface ElementKindPropsInput {
  /** Art aus clickTarget.elementKind, beim Totklick mit dem Zusatz '-disabled'. */
  elementKind: string;
}

const DIGIT_RUN = /\d+/g;
// Alles außerhalb dieser Zeichen fällt weg. Eckige Klammern bleiben, weil Formik/yup
// Listenfelder als 'services[3].price' meldet — mit der Ziffer als '#' bleibt der Pfad lesbar,
// ohne die Datensatz-Nummer preiszugeben.
const FORBIDDEN_IN_FIELD = /[^A-Za-z0-9_.#[\]-]/g;

/**
 * Macht aus einem Feld- oder Spaltennamen einen unverfänglichen Schlüssel: Ziffernfolgen werden
 * zu '#' (keine Datensatz-Nummern), alles Unerlaubte fällt weg. Nicht-Zeichenketten → ''.
 */
export const sanitizeFieldName = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  return value.replace(DIGIT_RUN, '#').replace(FORBIDDEN_IN_FIELD, '');
};

/** Name eines Ereignisses: nie leer, höchstens 128 Zeichen (batchSchema shortName). */
export const limitUsageName = (value: unknown): string => {
  if (typeof value !== 'string') return UNKNOWN_USAGE_NAME;
  const trimmed = value.trim();
  if (trimmed.length === 0) return UNKNOWN_USAGE_NAME;
  return trimmed.slice(0, MAX_USAGE_NAME_LENGTH);
};

/** Nichtnegative Ganzzahl oder null (= Feld entfällt). −0 wird zu 0. */
const count = (value: unknown): number | null => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const rounded = Math.round(value);
  if (rounded < 0) return null;
  return Math.max(0, rounded);
};

/** Pflichtzahl: ein unbrauchbarer Wert wird zu 0, damit das Ereignis gültig bleibt. */
const requiredCount = (value: unknown): number => {
  const result = count(value);
  return result === null ? 0 : result;
};

const buildColumn = (value: unknown): string => sanitizeFieldName(value).slice(0, MAX_COLUMN_LENGTH);

/**
 * Feldnamen bereinigen, leere wegwerfen, entdoppeln, sortieren und kommagetrennt zusammenfügen.
 * Gekappt wird an der letzten vollständigen Feldgrenze (kein '+N'-Rest), damit die Auswertung
 * jeden Eintrag als ganzen Feldnamen zählen kann.
 */
const joinInvalidFields = (fields: readonly string[] | undefined): string | undefined => {
  if (!fields || fields.length === 0) return undefined;
  const names = fields.map(sanitizeFieldName).filter((name) => name.length > 0);
  const unique = names.filter((name, index) => names.indexOf(name) === index).sort();
  const taken: string[] = [];
  let length = 0;
  let stopped = false;
  unique.forEach((name) => {
    if (stopped) return;
    const extra = taken.length === 0 ? name.length : name.length + 1;
    if (length + extra > MAX_INVALID_FIELDS_LENGTH) {
      stopped = true;
      return;
    }
    taken.push(name);
    length += extra;
  });
  return taken.length > 0 ? taken.join(',') : undefined;
};

/** props eines table-Ereignisses (Entwurf B). */
export const buildTableProps = (input: TablePropsInput): TableProps => {
  const props: TableProps = { op: input.op };
  const column = buildColumn(input.column);
  if (column.length > 0) props.column = column;
  const pageSize = count(input.page_size);
  if (pageSize !== null) props.page_size = pageSize;
  const page = count(input.page);
  if (page !== null) props.page = page;
  const hidden = count(input.hidden_count);
  if (hidden !== null) props.hidden_count = hidden;
  return props;
};

/** props eines form-Ereignisses (Entwurf C). */
export const buildFormProps = (input: FormPropsInput): FormProps => {
  const props: FormProps = { op: input.op };
  const msOpen = count(input.ms_open);
  if (msOpen !== null) props.ms_open = msOpen;
  const changed = count(input.changed_fields);
  if (changed !== null) props.changed_fields = changed;
  const invalid = joinInvalidFields(input.invalid_fields);
  if (invalid !== undefined) props.invalid_fields = invalid;
  return props;
};

/** props eines search-Ereignisses (Entwurf D). query_len und results sind Pflicht. */
export const buildSearchProps = (input: SearchPropsInput): SearchProps => {
  const props: SearchProps = { query_len: requiredCount(input.query_len), results: requiredCount(input.results) };
  const rank = count(input.selected_rank);
  if (rank !== null) props.selected_rank = rank;
  return props;
};

/** props eines dialog-Ereignisses (Entwurf E). */
export const buildDialogProps = (input: DialogPropsInput): DialogProps => ({ op: input.op });

/**
 * Art des Elements als einziges Zusatzfeld. Ein unbrauchbarer Wert wird zur leeren Zeichenkette:
 * batchSchema erlaubt sie (propString(16) ohne Mindestlänge), und die Auswertung zeigt dann
 * nichts — eine erfundene Art sähe dort aus wie eine gemessene.
 */
const buildElementKindProps = ({ elementKind }: ElementKindPropsInput): ElementKindProps => ({
  element_kind: typeof elementKind === 'string' ? elementKind.slice(0, MAX_ELEMENT_KIND_LENGTH) : '',
});

/** props eines rage_click-Ereignisses (RITA-1520, Entscheidung W1). */
export const buildRageClickProps = (input: ElementKindPropsInput): ElementKindProps => buildElementKindProps(input);

/** props eines dead_click-Ereignisses (RITA-1520, Entscheidung W3a). */
export const buildDeadClickProps = (input: ElementKindPropsInput): ElementKindProps => buildElementKindProps(input);
